import { Alert, Drawer, Modal, Skeleton } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useLang from "../../../../hooks/useLang";
import { getMobileView } from "../../../../redux/slices/isMobile";
import { convertUtcToLocal } from "../../../../library/utils";
import { DATE_TIME_FORMATS } from "../../../../utils/dateTimes";
import { getIncludedRecords, pickPrimaryIndex, resolveSelectedIndex } from "./mergeRecordsHelpers";

/**
 * The merge drawer shared by the opportunity merge (EAI-1815) and the lead
 * merge (EAI-1816). Both designs are the same screen with different nouns and
 * field rows, so the whole comparison UI lives here once and each caller
 * supplies its records, field config and copy.
 *
 * This component owns only the *selection*: which record is master, which are
 * ruled out, and which value wins per field. It never mutates anything — on
 * confirm it hands that state back through ``onConfirm`` so the caller can
 * build and run its own create mutation.
 *
 * Layout follows the design canvas: right-side drawer at
 * min(1200px, 100vw - 64px), a field-per-row grid whose columns are
 * Field | Master Record | one per participating record, an info panel, and a
 * left-aligned footer.
 */

const DRAWER_WIDTH = "min(1200px, 100vw - 64px)";
/** Design tokens, kept together so the grid cells stay consistent. */
const BORDER = "#F0F0F0";
const HEAD_BG = "#FAFAFA";
const SELECTED_BG = "#E6F4FF";
const BLUE = "#1677FF";
/** antd blue-7 — the id when it has no CRM record to link to. */
const DARK_BLUE = "#0958D9";
const INK = "rgba(0,0,0,0.88)";
/** ~65% black — the design's meta-text contrast floor. */
const META_INK = "rgba(0,0,0,0.65)";
const EXCLUDED_OPACITY = 0.35;

/**
 * The design's exclamation-circle glyph. Inline so the drawer carries no new
 * icon assets; `flipped` gives the info panel its ⓘ orientation.
 */
const ExclamationIcon = ({ size, color, flipped }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 1024 1024"
    fill={color}
    aria-hidden="true"
    focusable="false"
    className="flex-none"
    style={flipped ? { transform: "rotate(180deg)" } : undefined}
  >
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm-32 232c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v272c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V296zm32 440a48.01 48.01 0 0 1 0-96 48.01 48.01 0 0 1 0 96z" />
  </svg>
);

/** Enter/Space must drive any div we've given a role to. */
const onActivateKeyDown = handler => event => {
  if (!handler) return;
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    handler();
  }
};

const MergeRecordsDrawer = ({
  open,
  onClose,
  /** Drawer heading + the one-line instruction under it. */
  title,
  /** "A new lead is created with…" — what this particular merge does. */
  infoBody,
  /** "Merge these leads?" — the confirmation modal's heading. */
  confirmTitle,
  /** Confirmation body; `{{count}}` is replaced with the included count. */
  confirmBody,
  /** Shown when fewer than two records are left to compare. */
  emptyMessage,
  /** Participating records: the one the user opened this from, then duplicates. */
  records,
  /** Field config — see mergeRecordsHelpers. */
  fields,
  /** Creation-timestamp key on the records ("createdDate", "leadCreatedDate"). */
  createdKey = "createdDate",
  /**
   * Print each record's id in its header. Two duplicates of the same lead can
   * be identical on every compared field, so the id is the only thing that
   * tells the columns apart — and the only handle for matching one against the
   * CRM or a support ticket.
   */
  showRecordIds = false,
  /**
   * Optional `record -> { href, title }` for the id printed above. Returning a
   * href turns the id into an external link (the record in the dealer's CRM);
   * returning nothing leaves it as plain text, which is what happens for a
   * record that has no CRM counterpart.
   */
  getRecordIdLink,
  loading,
  error,
  merging,
  /** Receives the resolved selection so the caller can run its mutation. */
  onConfirm,
}) => {
  const [lang] = useLang();
  const isMobileView = useSelector(state => getMobileView(state));

  // Which record is the master, which records the user has ruled out, and the
  // per-field winner. `picks` is keyed by field key -> record index.
  const [primaryIdx, setPrimaryIdx] = useState(0);
  const [excluded, setExcluded] = useState({});
  const [picks, setPicks] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  /**
   * Most recently created record is the default master. Re-runs whenever the
   * drawer opens or the record set changes, which also clears any selection
   * left over from the previous record.
   */
  useEffect(() => {
    if (!open || !records?.length) return;
    setPrimaryIdx(pickPrimaryIndex(records, createdKey));
    setExcluded({});
    setPicks({});
    setConfirmOpen(false);
  }, [open, records, createdKey]);

  // A record takes part unless the user ruled it out; the master always does.
  const included = getIncludedRecords(records, primaryIdx, excluded);
  const includedCount = included.filter(Boolean).length;
  const duplicateCount = includedCount - 1;
  const canMerge = duplicateCount > 0 && !merging;

  const selectedIndexFor = fieldKey =>
    resolveSelectedIndex({ picks, fieldKey, included, primaryIdx });

  const setPrimary = index => {
    // Changing the master resets every field back to that record's values.
    setPrimaryIdx(index);
    setPicks({});
  };

  const toggleExcluded = index => {
    setExcluded(prev => ({ ...prev, [index]: !prev[index] }));
    setPicks({});
  };

  const handleConfirm = () => {
    // Guard here as well as on the button so a double-fire or a stale confirm
    // modal can't submit an empty or duplicate merge.
    if (!canMerge) return;
    setConfirmOpen(false);
    onConfirm?.({ records, primaryIdx, excluded, picks, included, includedCount });
  };

  const gridTemplateColumns = `120px 1.1fr ${(records || []).map(() => "1fr").join(" ")}`;

  const renderRecordHeader = (record, index) => {
    const isPrimary = index === primaryIdx;
    const isExcluded = !!excluded[index];
    const recordIdLink = showRecordIds ? getRecordIdLink?.(record) : null;
    return (
      <div
        key={record.id}
        className="flex flex-col gap-1 px-3 py-2.5"
        style={{ borderLeft: `1px solid ${BORDER}`, opacity: isExcluded ? EXCLUDED_OPACITY : 1 }}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-semibold" style={{ color: INK }}>
            {`${lang.mergeRecordLabel} ${index + 1}`}
          </span>
          {isPrimary && (
            <span
              className="rounded px-1.5 text-[13px] leading-5"
              style={{ background: SELECTED_BG, color: BLUE, border: "1px solid #91CAFF" }}
            >
              {lang.mergeMasterTag}
            </span>
          )}
          {/* An excluded record can't become the master — it isn't merging. */}
          {!isPrimary && !isExcluded && (
            <button
              type="button"
              className="cursor-pointer border-0 bg-transparent p-0 text-[13px]"
              style={{ color: BLUE }}
              onClick={() => setPrimary(index)}
            >
              {lang.mergeUseAsMasterRecord}
            </button>
          )}
        </div>
        {showRecordIds && record?.id != null && (
          <div className="text-xs leading-5" style={{ color: META_INK }}>
            {`${lang.mergeRecordIdLabel} `}
            {recordIdLink?.href ? (
              <a
                href={recordIdLink.href}
                target="_blank"
                rel="noreferrer"
                title={recordIdLink.title}
                className="underline"
                style={{ color: BLUE }}
              >
                {record.id}
              </a>
            ) : (
              // No CRM record to open, so the id can't be a link — keep it
              // prominent on its own instead of dropping to meta grey.
              <span className="font-semibold" style={{ color: DARK_BLUE }}>
                {record.id}
              </span>
            )}
          </div>
        )}
        <div className="text-xs leading-5" style={{ color: META_INK }}>
          {`${lang.mergeCreatedAt} `}
          {convertUtcToLocal(record?.[createdKey], DATE_TIME_FORMATS.MMM_DD_YYYY_COMMA_hh_mm_A)}
        </div>
        {!isPrimary && (
          <label
            className="flex cursor-pointer select-none items-center gap-1.5 text-xs leading-5"
            style={{ color: INK }}
          >
            <input
              type="checkbox"
              className="m-0"
              style={{ accentColor: BLUE }}
              checked={isExcluded}
              onChange={() => toggleExcluded(index)}
            />
            {lang.mergeDoNotMerge}
          </label>
        )}
      </div>
    );
  };

  const renderFieldRow = field => {
    const label = lang[field.langKey] || field.label;
    // A combined field offers no choice: the server carries every record's
    // values over to the master, so there is no winner to select and the
    // master column shows what the master already holds.
    const combined = !!field.combined;
    const selectedIndex = combined ? primaryIdx : selectedIndexFor(field.key);
    const masterValue = field.value(records[selectedIndex]) || "N/A";

    return (
      <div
        key={field.key}
        className="grid"
        style={{ gridTemplateColumns, borderBottom: `1px solid ${BORDER}` }}
        role={combined ? "group" : "radiogroup"}
        aria-label={label}
      >
        <div
          className="px-3 py-2.5 text-xs font-semibold"
          style={{ color: META_INK, background: HEAD_BG }}
        >
          {label}
        </div>
        <div
          className="px-3 py-2.5 text-[13px] font-semibold leading-5"
          style={{
            borderLeft: `1px solid ${BORDER}`,
            background: HEAD_BG,
            color: INK,
            overflowWrap: "anywhere",
          }}
        >
          {masterValue}
        </div>
        {records.map((record, index) => {
          const isExcluded = !included[index];
          const isSelected = !combined && !isExcluded && index === selectedIndex;
          const pick =
            combined || isExcluded
              ? undefined
              : () => setPicks(prev => ({ ...prev, [field.key]: index }));

          return (
            <div
              key={record.id}
              // Excluded columns stay visible for transparency but inert: no
              // radio, no handler, and out of the tab order. A combined field's
              // cells are inert for the same reason — nothing to choose.
              role={combined ? undefined : "radio"}
              aria-checked={combined ? undefined : isSelected}
              aria-label={`${label}: ${field.value(record) || "N/A"}`}
              aria-disabled={isExcluded || undefined}
              tabIndex={combined || isExcluded ? -1 : 0}
              onClick={pick}
              onKeyDown={onActivateKeyDown(pick)}
              className="flex items-start gap-2 px-3 py-2.5 text-[13px] leading-5"
              style={{
                borderLeft: `1px solid ${BORDER}`,
                cursor: combined || isExcluded ? "default" : "pointer",
                background: isSelected ? SELECTED_BG : "transparent",
                opacity: isExcluded ? EXCLUDED_OPACITY : 1,
              }}
            >
              {!combined && !isExcluded && (
                <span
                  className="mt-0.5 h-4 w-4 flex-none rounded-full bg-white"
                  style={{ border: isSelected ? `4px solid ${BLUE}` : "1px solid #D9D9D9" }}
                />
              )}
              <span className="min-w-0" style={{ overflowWrap: "anywhere", color: INK }}>
                {field.value(record) || "N/A"}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderBody = () => {
    // `records` always holds the record this was opened from, so gate on the
    // duplicates still being in flight — otherwise the "nothing to merge"
    // notice flashes before they arrive.
    if (loading && (records?.length || 0) < 2) {
      return <Skeleton active paragraph={{ rows: 8 }} title={false} />;
    }
    if (error) {
      return <Alert type="error" showIcon message={error.message || lang.somethingWentWrong} />;
    }
    // Every flagged duplicate has since been merged or removed.
    if ((records?.length || 0) < 2) {
      return <Alert type="info" showIcon message={emptyMessage} />;
    }

    return (
      <>
        <div
          className="mb-4 inline-flex w-fit items-center gap-2 rounded-md px-3 py-2"
          style={{ border: "1px solid #FFE58F", background: "#FFFBE6" }}
        >
          <ExclamationIcon size={14} color="#FAAD14" />
          <span className="text-[13px] leading-5" style={{ color: INK }}>
            {lang.mergeNotDuplicateHint}
          </span>
        </div>

        <div className="overflow-hidden rounded-lg" style={{ border: `1px solid ${BORDER}` }}>
          <div
            className="grid"
            style={{
              gridTemplateColumns,
              background: HEAD_BG,
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <div className="px-3 py-2.5" />
            <div className="px-3 py-2.5" style={{ borderLeft: `1px solid ${BORDER}` }}>
              <div className="text-sm font-semibold" style={{ color: INK }}>
                {lang.mergeMasterRecord}
              </div>
            </div>
            {records.map(renderRecordHeader)}
          </div>
          {fields.map(renderFieldRow)}
        </div>

        <div
          className="mt-4 flex items-start gap-2.5 rounded-lg p-3 text-[13px] leading-5"
          style={{ border: `1px solid ${BORDER}`, background: HEAD_BG, color: META_INK }}
        >
          <span className="mt-0.5 flex-none">
            <ExclamationIcon size={16} color={BLUE} flipped />
          </span>
          <div className="min-w-0">
            <div className="mb-0.5 font-semibold" style={{ color: INK }}>
              {lang.mergeInfoPanelTitle}
            </div>
            <div>{infoBody}</div>
          </div>
        </div>
      </>
    );
  };

  const mergeLabel =
    duplicateCount > 0
      ? lang.mergeRecordsAction.replace("{{count}}", includedCount)
      : lang.mergeAction;

  const renderFooter = () => (
    <div className="flex items-center gap-2">
      <span
        role="button"
        tabIndex={canMerge ? 0 : -1}
        aria-disabled={!canMerge}
        onClick={() => canMerge && setConfirmOpen(true)}
        onKeyDown={onActivateKeyDown(canMerge ? () => setConfirmOpen(true) : null)}
        className="inline-flex h-8 select-none items-center rounded-md px-[15px] text-[13px] text-white"
        style={{
          background: canMerge ? BLUE : "rgba(0,0,0,0.25)",
          cursor: canMerge ? "pointer" : "not-allowed",
        }}
      >
        {merging ? `${mergeLabel}…` : mergeLabel}
      </span>
      <span
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={onActivateKeyDown(onClose)}
        className="inline-flex h-8 cursor-pointer items-center rounded-md bg-white px-[15px] text-[13px]"
        style={{ border: "1px solid #D9D9D9", color: INK }}
      >
        {lang.cancel}
      </span>
      <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: "#D48806" }}>
        <ExclamationIcon size={13} color="#FAAD14" />
        {lang.mergeCannotBeUndone}
      </span>
    </div>
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        className="normal-popup"
        width={isMobileView ? "100%" : DRAWER_WIDTH}
        title={
          <div>
            <div className="text-base font-medium" style={{ color: INK }}>
              {title}
            </div>
            <div className="mt-0.5 text-[13px] font-normal" style={{ color: META_INK }}>
              {lang.mergeSubtitle}
            </div>
          </div>
        }
        styles={{ body: { padding: "16px 24px" }, footer: { padding: "12px 24px" } }}
        footer={(records?.length || 0) >= 2 ? renderFooter() : null}
        destroyOnClose
      >
        {renderBody()}
      </Drawer>

      <Modal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        title={confirmTitle}
        width={416}
        style={{ top: 120 }}
        centered={false}
        destroyOnClose
        footer={
          <div className="flex justify-start gap-2">
            <span
              role="button"
              tabIndex={0}
              onClick={handleConfirm}
              onKeyDown={onActivateKeyDown(handleConfirm)}
              aria-disabled={!canMerge}
              className="inline-flex h-8 items-center rounded-md px-[15px] text-[13px] text-white"
              style={{
                background: canMerge ? BLUE : "rgba(0,0,0,0.25)",
                cursor: canMerge ? "pointer" : "not-allowed",
              }}
            >
              {lang.mergeConfirmYes}
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={() => setConfirmOpen(false)}
              onKeyDown={onActivateKeyDown(() => setConfirmOpen(false))}
              className="inline-flex h-8 cursor-pointer items-center rounded-md bg-white px-[15px] text-[13px]"
              style={{ border: "1px solid #D9D9D9", color: INK }}
            >
              {lang.cancel}
            </span>
          </div>
        }
      >
        <div className="text-[13px] leading-5" style={{ color: META_INK }}>
          {confirmBody?.replace("{{count}}", includedCount)}
        </div>
      </Modal>
    </>
  );
};

export default MergeRecordsDrawer;
